import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_SESSION, USE_MOCKS } from '@/mocks/fixtures'
import type { CurrentUser } from '@/types/domain'

/* The caller's own password change (QW-5): `PUT /api/session/password`.
 *
 * The server verifies the current password (a wrong one is a 422 field code
 * `currentPassword: incorrect`, never a 401, and counts toward the sign-in
 * throttle), enforces its policy on the new one (`too-short`, `too-long`,
 * `unchanged`), clears `mustChangePassword`, and ROTATES the session: the
 * response sets a new cookie and every other session of this account is gone.
 * The body is the `CurrentUser`, unflagged.
 *
 * On success the session cache is SEEDED with that body, as sign-in does, and
 * this runs before any `mutate(…, { onSuccess })` the page passes — TanStack
 * runs the hook's callback first. The order is load-bearing: the page
 * navigates home in its own callback, and RequireSession reads the cached
 * session; a cache still saying "flagged" would send the reader straight back
 * to the change page. Invalidating instead would leave that window open for a
 * round trip. Nothing else is cleared — it is the same person, and nothing
 * they had cached was read under a different identity.
 */

export interface PasswordChange {
  currentPassword: string
  newPassword: string
}

export function useChangePassword() {
  const queryClient = useQueryClient()

  return useMutation<CurrentUser, unknown, PasswordChange>({
    mutationFn: (change) =>
      USE_MOCKS
        ? Promise.resolve(MOCK_SESSION)
        : api.put<CurrentUser>('/session/password', change),

    onSuccess: (user) => {
      queryClient.setQueryData(queryKeys.session, user)
    },
  })
}
